import type { ActivityLog } from '../types/typesActivityLog';
import type { ActivityType } from '../types/typesActivityLog';
import type { Repair, SparePartWithQty } from '../types/typesRepair';
import type { Customer } from '../types/typesCustomer';
import {
  formatRepairStatusForDisplay,
  repairStatusesEquivalent,
} from './repairStatusDisplay';

const REPAIR_FIELD_LABELS: Record<string, string> = {
  status: 'סטטוס',
  employeeName: 'טכנאי',
  storeName: 'חנות',
  deviceModel: 'דגם',
  issue: 'תיאור תקלה',
  issueTypes: 'סוגי תקלה',
  customerName: 'שם לקוח',
  customerPhone: 'טלפון',
  customerCompany: 'חברה',
  paymentMethod: 'אמצעי תשלום',
  paymentAmount: 'סכום גבייה',
  paymentStatus: 'סטטוס תשלום',
  paymentDate: 'תאריך גבייה',
  paymentHandledByName: 'מטפל בגבייה',
  paymentRefundedAmount: 'סכום שהוחזר',
  serviceSummary: 'סיכום שירות',
  managerNote: 'הערת מנהל',
  labSummary: 'סיכום מעבדה',
  labResultStatus: 'סטטוס מעבדה',
  labPrice: 'מחיר מעבדה',
  serialNumber: 'מספר סידורי',
  receivedDate: 'תאריך קליטה',
  dueDate: 'תאריך יעד',
  technicianCompletedDate: 'תאריך סיום טיפול',
  storeConfirmed: 'אישור קבלה בחנות',
  priorityInvoiceIssued: 'חשבונית פריוריטי',
  creditInvoiceIssued: 'חשבונית זיכוי',
  sparePartsWithQty: 'חלקי חילוף',
};

const CUSTOMER_FIELD_LABELS: Record<string, string> = {
  name: 'שם',
  firstName: 'שם פרטי',
  lastName: 'שם משפחה',
  phone: 'טלפון',
  email: 'אימייל',
  city: 'עיר',
  company: 'חברה',
  store: 'חנות',
  active: 'פעיל',
  notes: 'הערות',
  idNumber: 'ת.ז.',
};

const METADATA_LABELS: Record<string, string> = {
  paymentAmount: 'סכום',
  paymentMethod: 'אמצעי תשלום',
  gateway: 'שער תשלום',
  source: 'מקור',
  refundAmount: 'סכום החזר',
  totalRefunded: 'סה״כ הוחזר',
  chargedAmount: 'סכום מקורי',
  refundType: 'סוג החזר',
  partName: 'חלק',
  partId: 'מז׳ חלק',
  adjustment: 'שינוי כמות',
  currentStock: 'מלאי לפני',
  newStock: 'מלאי אחרי',
  initialStatus: 'סטטוס',
  customerName: 'לקוח',
  deviceModel: 'דגם',
  deletedStatus: 'סטטוס שנמחק',
  deletedCustomer: 'לקוח שנמחק',
  deletedDevice: 'דגם שנמחק',
  deletedStore: 'חנות שנמחקה',
  deletedPaymentAmount: 'סכום גבייה שנמחק',
  createdUserName: 'שם משתמש',
  createdUserEmail: 'אימייל',
  storeName: 'חנות',
  role: 'תפקיד',
  revertedFalseMark: 'ביטול סימון שולם',
  referenceNumber: 'מספר קריאה',
};

function formatSparePartsSummary(parts: SparePartWithQty[] | undefined): string {
  if (!parts?.length) return '(ריק)';
  return parts
    .map((p) => {
      const name = String(p.part?.description || p.part?.id || '?').trim();
      const qty = Number(p.quantity || 0);
      return `${name} ×${qty}`;
    })
    .join(', ');
}

function sparePartsSignature(parts: SparePartWithQty[] | undefined): string {
  if (!parts?.length) return '';
  return parts
    .map((p) => `${String(p.part?.id || '').trim()}:${Number(p.quantity || 0)}`)
    .sort()
    .join('|');
}

function formatFieldValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return '(ריק)';
  if (field === 'status') {
    return formatRepairStatusForDisplay(String(value));
  }
  if (field === 'sparePartsWithQty') {
    return formatSparePartsSummary(value as SparePartWithQty[]);
  }
  if (field === 'paymentAmount' || field === 'paymentRefundedAmount' || field === 'labPrice') {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? `₪${n.toFixed(2)}` : '(ריק)';
  }
  if (field === 'storeConfirmed' || field === 'priorityInvoiceIssued' || field === 'creditInvoiceIssued') {
    return value ? 'כן' : 'לא';
  }
  if (field === 'issueTypes' && Array.isArray(value)) {
    const list = value.map((x) => String(x).trim()).filter(Boolean);
    return list.length > 0 ? list.join(', ') : '(ריק)';
  }
  if (typeof value === 'boolean') return value ? 'כן' : 'לא';
  const text = String(value).trim();
  return text || '(ריק)';
}

/** שדות משמעותיים ללוג — בלי מזהים טכניים / חותמות זמן פנימיות */
export function pickRepairActivitySnapshot(
  repair: Partial<Repair> | null | undefined,
): Record<string, unknown> {
  if (!repair) return {};
  return {
    status: repair.status,
    employeeName: repair.employeeName,
    storeName: repair.storeName,
    deviceModel: repair.deviceModel,
    issue: repair.issue,
    issueTypes: repair.issueTypes,
    customerName: repair.customerName,
    customerPhone: repair.customerPhone,
    customerCompany: repair.customerCompany,
    paymentMethod: repair.paymentMethod,
    paymentAmount: repair.paymentAmount,
    paymentStatus: repair.paymentStatus,
    paymentDate: (repair as { paymentDate?: string }).paymentDate,
    paymentHandledByName: repair.paymentHandledByName,
    paymentRefundedAmount: repair.paymentRefundedAmount,
    serviceSummary: repair.serviceSummary,
    managerNote: repair.managerNote,
    labSummary: repair.labSummary,
    labResultStatus: repair.labResultStatus,
    labPrice: repair.labPrice,
    serialNumber: repair.serialNumber,
    receivedDate: repair.receivedDate,
    dueDate: repair.dueDate,
    technicianCompletedDate: repair.technicianCompletedDate,
    storeConfirmed: Boolean(repair.storeConfirmed),
    priorityInvoiceIssued: Boolean(repair.priorityInvoiceIssued),
    creditInvoiceIssued: Boolean(repair.creditInvoiceIssued),
    sparePartsWithQty: sparePartsSignature(repair.sparePartsWithQty),
  };
}

export function pickCustomerActivitySnapshot(
  customer: Partial<Customer> | null | undefined,
): Record<string, unknown> {
  if (!customer) return {};
  return {
    name: customer.name,
    firstName: customer.firstName,
    lastName: customer.lastName,
    phone: customer.phone,
    email: customer.email,
    city: customer.city,
    company: customer.company,
    store: customer.store,
    active: customer.active,
    notes: customer.notes,
    idNumber: customer.idNumber,
  };
}

export function collectCustomerActivityChanges(
  before: Partial<Customer> | null | undefined,
  after: Partial<Customer> | null | undefined,
): ActivityChangeDetail[] {
  const beforeRaw = pickCustomerActivitySnapshot(before);
  const afterRaw = pickCustomerActivitySnapshot(after);
  const changes: ActivityChangeDetail[] = [];
  for (const field of Object.keys(CUSTOMER_FIELD_LABELS)) {
    const from = formatFieldValue(field, beforeRaw[field]);
    const to = formatFieldValue(field, afterRaw[field]);
    if (from !== to) {
      changes.push({
        field,
        label: CUSTOMER_FIELD_LABELS[field],
        from,
        to,
      });
    }
  }
  return changes;
}

export interface ActivityChangeDetail {
  field: string;
  label: string;
  from: string;
  to: string;
}

function formatChangeLine(label: string, from: string, to: string): string {
  if (from === to) return '';
  if (!label) return `${from} → ${to}`;
  return `${label}: ${from} → ${to}`;
}

function normalizeLoggedChangeValue(label: string, value: string): string {
  const trimmed = String(value || '').trim();
  if (!trimmed || trimmed === '(ריק)') return trimmed || '(ריק)';
  if (label === 'סטטוס') return formatRepairStatusForDisplay(trimmed);
  return trimmed;
}

function sanitizeActivityChanges(changes: ActivityChangeDetail[]): ActivityChangeDetail[] {
  return changes
    .filter((c) => {
      if (c.field === 'status' && repairStatusesEquivalent(c.from, c.to)) return false;
      const from = normalizeLoggedChangeValue(c.label, c.from);
      const to = normalizeLoggedChangeValue(c.label, c.to);
      return from !== to;
    })
    .map((c) => ({
      ...c,
      from: normalizeLoggedChangeValue(c.label, c.from),
      to: normalizeLoggedChangeValue(c.label, c.to),
    }));
}

function buildChangesSummaryDescription(changes: ActivityChangeDetail[]): string {
  const sanitized = sanitizeActivityChanges(changes);
  if (sanitized.length === 0) return 'עדכון';
  if (sanitized.length === 1) {
    const line = formatChangeLine(sanitized[0].label, sanitized[0].from, sanitized[0].to);
    return line || 'עדכון';
  }
  const statusChange = sanitized.find((c) => c.field === 'status');
  if (statusChange) {
    const others = sanitized.length - 1;
    const main = formatChangeLine(statusChange.label, statusChange.from, statusChange.to);
    return others > 0 && main ? `${main} (+${others} שדות)` : main || 'עדכון';
  }
  const preview = sanitized
    .slice(0, 2)
    .map((c) => formatChangeLine(c.label, c.from, c.to))
    .filter(Boolean)
    .join(' · ');
  return sanitized.length > 2 ? `${preview} · +${sanitized.length - 2}` : preview || 'עדכון';
}

const INVENTORY_ACTIVITY_TYPES = new Set<ActivityLog['activityType']>([
  'inventory_deducted',
  'inventory_returned',
  'inventory_adjusted',
  'inventory_received',
  'inventory_stock_count',
]);

function isInventoryActivityType(type: ActivityLog['activityType']): boolean {
  return INVENTORY_ACTIVITY_TYPES.has(type);
}

/** תיאור קצר — שם החלק (לא שינוי מלאי) */
function formatInventoryPartDescription(log: ActivityLog): string {
  const meta = log.metadata || {};
  const partName = String(meta.partName || '').trim();
  const adjustment = Number(meta.adjustment);
  const qty = Number.isFinite(adjustment) ? Math.abs(adjustment) : 0;

  if (partName) {
    if (log.activityType === 'inventory_deducted') {
      return qty > 0 ? `${partName} (×${qty})` : partName;
    }
    if (log.activityType === 'inventory_returned') {
      return qty > 0 ? `החזרה: ${partName} (×${qty})` : `החזרה: ${partName}`;
    }
    if (log.activityType === 'inventory_received') {
      return qty > 0 ? `קליטה: ${partName} (×${qty})` : `קליטה: ${partName}`;
    }
    return partName;
  }

  const raw = String(log.description || '').trim();
  const legacy = raw.match(
    /^(?:הורדה ממלאי|החזרה למלאי|קליטת מלאי|התאמת מלאי(?: עבור)?)\s*:?\s*(.+)$/u,
  );
  if (legacy?.[1]) {
    return legacy[1].replace(/\s*\(כמות:\s*\d+\)\s*$/u, '').trim();
  }

  return raw || '-';
}

/** פרטים נוספים — שינוי מלאי */
function formatInventoryStockDetails(log: ActivityLog): string {
  const meta = log.metadata || {};
  const metaChanges = meta.changes;
  if (Array.isArray(metaChanges) && metaChanges.length > 0) {
    const lines = metaChanges
      .map((c) => {
        const label = String((c as { label?: string }).label || 'מלאי').trim();
        const from = String((c as { from?: string }).from ?? '').trim();
        const to = String((c as { to?: string }).to ?? '').trim();
        if (!from && !to) return '';
        return `${label}: ${from} → ${to}`;
      })
      .filter(Boolean);
    if (lines.length > 0) return lines.join('\n');
  }

  if (log.previousValue != null && log.newValue != null) {
    return `מלאי: ${log.previousValue} → ${log.newValue}`;
  }

  if (meta.currentStock != null && meta.newStock != null) {
    return `מלאי: ${meta.currentStock} → ${meta.newStock}`;
  }

  return '-';
}

export function formatActivityLogDescription(log: ActivityLog): string {
  if (log.activityType === 'user_login' || log.activityType === 'user_logout') {
    return String(log.description || '').trim() || translateFallback(log.activityType);
  }

  if (log.activityType === 'repair_viewed') {
    const raw = String(log.description || '').trim();
    if (raw) return raw;
    const call = String(log.repairCallNumber || '').trim();
    return call ? `צפה בקריאה ${call}` : 'צפה בקריאה';
  }

  if (isInventoryActivityType(log.activityType)) {
    return formatInventoryPartDescription(log);
  }

  const metaChanges = log.metadata?.changes;
  if (Array.isArray(metaChanges) && metaChanges.length > 0) {
    const changes = sanitizeActivityChanges(
      metaChanges
        .map((c) => {
          const label = String((c as { label?: string }).label || '').trim();
          const from = String((c as { from?: string }).from ?? '').trim();
          const to = String((c as { to?: string }).to ?? '').trim();
          if (!label) return null;
          const field = label === 'סטטוס' ? 'status' : label;
          return { field, label, from, to };
        })
        .filter(Boolean) as ActivityChangeDetail[],
    );
    if (changes.length > 0) {
      const summary = buildChangesSummaryDescription(changes);
      const call = String(log.repairCallNumber || '').trim();
      if (call && (log.activityType === 'repair_updated' || log.activityType === 'status_changed')) {
        return `קריאה ${call}: ${summary}`;
      }
      return summary;
    }
  }

  if (log.previousValue != null && log.newValue != null) {
    const from = normalizeLoggedChangeValue(
      log.activityType === 'status_changed' ? 'סטטוס' : '',
      String(log.previousValue).trim(),
    );
    const to = normalizeLoggedChangeValue(
      log.activityType === 'status_changed' ? 'סטטוס' : '',
      String(log.newValue).trim(),
    );
    if (from || to) {
      if (log.activityType === 'status_changed') {
        if (repairStatusesEquivalent(from, to)) return 'עדכון';
        const line = formatChangeLine('סטטוס', from, to) || `${from} → ${to}`;
        const call = String(log.repairCallNumber || '').trim();
        return call ? `קריאה ${call}: ${line}` : line;
      }
      return formatChangeLine('', from, to).replace(/^: /, '') || `${from} → ${to}`;
    }
  }

  const raw = String(log.description || '').trim();
  if (!raw) return '-';

  const stripped = raw
    .replace(/^(עודכנ[הת]|נוצר[ה]?|נמחק[ה]?)\s+קריא(?:ה|ת\s+שירות)\s+\d+\s*:?\s*/u, '')
    .replace(/^(עודכנ[הת]|נוצר[ה]?|נמחק[ה]?)\s+לקוח\s+[^:]+:\s*/u, '')
    .replace(/\s+\d{6,}\s*$/u, '')
    .trim();
  if (stripped) return stripped;

  // רשומות ישנות: «עודכנה קריאת שירות 123456» בלי metadata.changes
  if (/^עודכנ/u.test(raw)) {
    const legacyField = raw.match(/:\s*(.+)$/u)?.[1]?.trim();
    if (legacyField) return legacyField;
    return 'עדכון קריאה (רשומה ישנה — ללא פירוט שדות)';
  }

  return raw;
}

function translateFallback(activityType: ActivityLog['activityType']): string {
  if (activityType === 'user_login') return 'התחבר למערכת';
  if (activityType === 'user_logout') return 'התנתק מהמערכת';
  return '-';
}

export function buildCustomerUpdateActivityLog(
  customerLabel: string,
  before: Partial<Customer> | null | undefined,
  after: Partial<Customer> | null | undefined,
): {
  activityType: ActivityType;
  description: string;
  previousValue?: string;
  newValue?: string;
  metadata?: Record<string, unknown>;
} | null {
  const changes = collectCustomerActivityChanges(before, after);
  if (changes.length === 0) return null;
  const primary = changes[0];
  const description = buildChangesSummaryDescription(changes);
  return {
    activityType: 'customer_updated',
    description,
    previousValue: primary.from,
    newValue: primary.to,
    metadata: {
      changes: changes.map(({ label, from, to }) => ({ label, from, to })),
    },
  };
}

export function collectRepairActivityChanges(
  before: Partial<Repair> | null | undefined,
  after: Partial<Repair> | null | undefined,
): ActivityChangeDetail[] {
  const beforeRaw = pickRepairActivitySnapshot(before);
  const afterRaw = pickRepairActivitySnapshot(after);
  const changes: ActivityChangeDetail[] = [];

  for (const field of Object.keys(REPAIR_FIELD_LABELS)) {
    if (field === 'status') {
      if (repairStatusesEquivalent(before?.status, after?.status)) continue;
    }
    const from = formatFieldValue(
      field,
      field === 'sparePartsWithQty'
        ? before?.sparePartsWithQty
        : beforeRaw[field],
    );
    const to = formatFieldValue(
      field,
      field === 'sparePartsWithQty' ? after?.sparePartsWithQty : afterRaw[field],
    );
    if (from !== to) {
      changes.push({
        field,
        label: REPAIR_FIELD_LABELS[field],
        from,
        to,
      });
    }
  }

  return changes;
}

export function buildRepairUpdateActivityLog(
  callNumber: string,
  before: Partial<Repair> | null | undefined,
  after: Partial<Repair> | null | undefined,
): {
  activityType: ActivityType;
  description: string;
  previousValue?: string;
  newValue?: string;
  metadata?: Record<string, unknown>;
} | null {
  const changes = sanitizeActivityChanges(collectRepairActivityChanges(before, after));
  if (changes.length === 0) return null;

  const statusChange = changes.find((c) => c.field === 'status');
  const description = buildChangesSummaryDescription(changes);

  const activityType: ActivityType =
    statusChange && changes.length === 1 ? 'status_changed' : 'repair_updated';

  const primary = statusChange ?? (changes.length === 1 ? changes[0] : undefined);

  return {
    activityType,
    description,
    previousValue: primary?.from,
    newValue: primary?.to,
    metadata: {
      changes: changes.map(({ label, from, to }) => ({ label, from, to })),
    },
  };
}

export function formatActivityLogDetails(log: ActivityLog): string {
  if (log.activityType === 'user_login' || log.activityType === 'user_logout') {
    const parts: string[] = [];
    if (log.actorType === 'external') parts.push('סוג: משתמש חיצוני');
    else if (log.actorType === 'employee') parts.push('סוג: עובד');
    if (log.storeName) parts.push(`חנות: ${log.storeName}`);
    if (log.userEmail) parts.push(`אימייל: ${log.userEmail}`);
    if (log.actorRole) parts.push(`תפקיד: ${log.actorRole}`);
    return parts.length > 0 ? parts.join('\n') : '-';
  }

  if (log.activityType === 'repair_viewed') {
    const meta = log.metadata || {};
    const parts: string[] = [];
    if (meta.customerName) parts.push(`לקוח: ${String(meta.customerName)}`);
    if (meta.deviceModel) parts.push(`מכשיר: ${String(meta.deviceModel)}`);
    if (meta.status) parts.push(`סטטוס: ${formatRepairStatusForDisplay(String(meta.status))}`);
    if (log.storeName || meta.storeName) parts.push(`חנות: ${String(log.storeName || meta.storeName)}`);
    if (log.actorType === 'external') parts.push('סוג צופה: משתמש חיצוני');
    return parts.length > 0 ? parts.join('\n') : '-';
  }

  if (isInventoryActivityType(log.activityType)) {
    return formatInventoryStockDetails(log);
  }

  const metaChanges = log.metadata?.changes;
  if (Array.isArray(metaChanges) && metaChanges.length > 0) {
    const changes = sanitizeActivityChanges(
      metaChanges
        .map((c) => {
          const label = String((c as { label?: string }).label || '').trim();
          const from = String((c as { from?: string }).from ?? '').trim();
          const to = String((c as { to?: string }).to ?? '').trim();
          if (!label) return null;
          return {
            field: label === 'סטטוס' ? 'status' : label,
            label,
            from,
            to,
          };
        })
        .filter(Boolean) as ActivityChangeDetail[],
    );
    if (changes.length > 0) {
      return changes
        .map((c) => formatChangeLine(c.label, c.from, c.to))
        .filter(Boolean)
        .join('\n');
    }
  }

  if (log.previousValue && log.newValue) {
    const label = log.activityType === 'status_changed' ? 'סטטוס' : '';
    const from = normalizeLoggedChangeValue(label, String(log.previousValue));
    const to = normalizeLoggedChangeValue(label, String(log.newValue));
    if (log.activityType === 'status_changed' && repairStatusesEquivalent(from, to)) {
      return '-';
    }
    const line = label
      ? formatChangeLine(label, from, to)
      : formatChangeLine('', from, to).replace(/^: /, '');
    return line || `${from} → ${to}`;
  }

  const meta = log.metadata || {};
  const parts: string[] = [];

  if (meta.paymentAmount != null && Number(meta.paymentAmount) > 0) {
    parts.push(`סכום: ₪${Number(meta.paymentAmount).toFixed(2)}`);
  }
  if (meta.paymentMethod) {
    parts.push(`אמצעי: ${String(meta.paymentMethod)}`);
  }
  if (meta.refundAmount != null) {
    parts.push(`החזר: ₪${Number(meta.refundAmount).toFixed(2)}`);
  }
  if (meta.refundType) {
    parts.push(`סוג: ${meta.refundType === 'refund_full' ? 'מלא' : 'חלקי'}`);
  }
  if (meta.deletedStatus) {
    parts.push(`סטטוס: ${formatRepairStatusForDisplay(String(meta.deletedStatus))}`);
  }
  if (meta.deletedCustomer) {
    parts.push(`לקוח: ${String(meta.deletedCustomer)}`);
  }
  if (meta.deletedDevice) {
    parts.push(`דגם: ${String(meta.deletedDevice)}`);
  }
  if (meta.partName) {
    parts.push(`חלק: ${String(meta.partName)}`);
  }
  if (meta.adjustment != null) {
    parts.push(`שינוי: ${meta.adjustment}`);
  }
  if (meta.initialStatus) {
    parts.push(`סטטוס: ${formatRepairStatusForDisplay(String(meta.initialStatus))}`);
  }
  if (meta.customerName) {
    parts.push(`לקוח: ${String(meta.customerName)}`);
  }
  if (meta.deviceModel) {
    parts.push(`דגם: ${String(meta.deviceModel)}`);
  }
  if (log.storeName) {
    parts.push(`חנות: ${log.storeName}`);
  }

  const knownKeys = new Set([
    'changes',
    'paymentAmount',
    'paymentMethod',
    'gateway',
    'source',
    'refundAmount',
    'totalRefunded',
    'chargedAmount',
    'refundType',
    'partName',
    'partId',
    'adjustment',
    'currentStock',
    'newStock',
    'initialStatus',
    'customerName',
    'deviceModel',
    'deletedStatus',
    'deletedCustomer',
    'deletedDevice',
    'deletedStore',
    'deletedPaymentAmount',
    'createdUserId',
    'createdUserName',
    'createdUserEmail',
    'createdUserPhone',
    'storeId',
    'storeName',
    'role',
    'revertedFalseMark',
    'referenceId',
    'referenceNumber',
    'actorType',
    'status',
    'kind',
    'channel',
    'target',
  ]);
  for (const [key, value] of Object.entries(meta)) {
    if (knownKeys.has(key) || value === null || value === undefined || value === '') continue;
    const label = METADATA_LABELS[key] || key;
    if (typeof value === 'boolean') {
      parts.push(`${label}: ${value ? 'כן' : 'לא'}`);
    } else {
      parts.push(`${label}: ${String(value)}`);
    }
  }

  if (parts.length > 0) return parts.join('\n');
  return '-';
}
